import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ownerPath } from '@/lib/hosts';
import { useSubNav } from '@/contexts/SubNavContext';
import type { NavItem } from '@/lib/navigation';

interface SidebarItemProps {
  icon: LucideIcon;
  labelKey: string;
  label?: string;
  href: string;
  isActive: boolean;
  /** Whether this item has children that should drive the persistent subnav panel. */
  hasFlyout?: boolean;
  /** Full NavItem reference — used by the SubNav toggle to know children. */
  navItem?: NavItem;
}

/**
 * Single rail item (icon + label vertical).
 *
 * Phase 8 iteration 4 :
 * - Items WITH children → click TOGGLES the persistent `<RailSubnavPanel>`
 *   (open ↔ collapsed) AND navigates to the parent href (so the user sees the
 *   first sub-section by default). Hover flyout pattern removed.
 * - Items WITHOUT children → behaves like a normal Link. Also closes any
 *   currently-open subnav.
 */
export function SidebarItem({
  icon: Icon,
  labelKey,
  label,
  href,
  isActive,
  hasFlyout,
  navItem,
}: SidebarItemProps) {
  const { t } = useTranslation(['navigation']);
  const { toggleFromRail, activeKey, state } = useSubNav();

  const isPanelOpenForThis = activeKey === labelKey && state === 'open';

  const handleClick = () => {
    if (navItem) toggleFromRail(navItem);
  };

  return (
    <div className="relative flex w-[76px] items-stretch">
      <NavLink
        to={ownerPath(href)}
        onClick={handleClick}
        aria-current={isActive ? 'page' : undefined}
        aria-expanded={hasFlyout ? isPanelOpenForThis : undefined}
        className={({ isPending }) =>
          cn(
            'relative flex w-full flex-col items-center gap-1 rounded-lg px-1 py-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800 dark:hover:text-slate-50',
            isActive && 'bg-lime-50 text-charcoal-950 dark:bg-lime-500/12 dark:text-lime-300',
            isPending && 'animate-pulse bg-lime-50/70 text-charcoal-950',
          )
        }
      >
        {isActive ? (
          <span
            aria-hidden="true"
            className="absolute -left-1.5 top-3 bottom-3 w-[3px] rounded-full bg-lime-400"
          />
        ) : null}
        <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
        <span className="text-[10px] font-medium leading-tight">
          {label ?? t(`navigation:${labelKey}`)}
        </span>
        {hasFlyout ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute right-2 top-2 h-1 w-1 rounded-full transition-colors',
              isPanelOpenForThis ? 'bg-lime-500' : 'bg-slate-300 dark:bg-slate-600',
            )}
          />
        ) : null}
      </NavLink>
    </div>
  );
}
